import { A } from "@solidjs/router";
import { Show } from "solid-js";
import { GOVERNANCE_STATE } from "../../enums/govern";
import type { DeliveryBrief } from "../brief/briefTypes";
import { GovernActionBar } from "./GovernActionBar";
import { GovernChecks } from "./GovernChecks";
import { GovernDeliveryNotes } from "./GovernDeliveryNotes";
import { GovernRoles } from "./GovernRoles";
import { verificationEditingMessage } from "./governGuidance";
import {
  canEditVerification,
  canVerifyWork,
  governanceStateLabel,
  sourceBriefReady,
} from "./governStatus";
import type { GovernEditor, GovernWorkItem } from "./governTypes";

export function GovernPlan(props: {
  work: GovernWorkItem;
  currentBrief: DeliveryBrief | null;
  onOpenEditor: (editor: GovernEditor, trigger: HTMLElement) => void;
  onUpdate: (update: (work: GovernWorkItem) => GovernWorkItem) => void;
}) {
  const sourceReady = () => sourceBriefReady(props.currentBrief);
  const setState = (state: GovernWorkItem["state"]) =>
    props.onUpdate((work) => ({ ...work, state }));
  return (
    <article class="govern-plan">
      <header class="govern-work-head">
        <div>
          <span class="signal-mini-kicker">
            {props.work.sourceSnapshot.source.label}
          </span>
          <h2>{props.work.sourceSnapshot.title}</h2>
          <p>{props.work.sourceSnapshot.objective}</p>
        </div>
        <span class={`govern-state is-${props.work.state}`}>
          {governanceStateLabel(props.work.state)}
        </span>
      </header>

      <Show when={!props.currentBrief || !sourceReady()}>
        <section class="govern-source-warning" role="status">
          <strong>
            {props.currentBrief
              ? "Source Brief is no longer ready"
              : "Source Brief is unavailable"}
          </strong>
          <p>
            {props.currentBrief
              ? "Resolve it in Brief before verification can continue. Existing governance notes and evidence remain available."
              : "This session can retain the work context, but final verification requires the source Brief."}
          </p>
          <A href={`/w/${props.work.websiteId}/brief`}>Open Brief</A>
        </section>
      </Show>

      <section class="govern-work-summary">
        <div>
          <span>Outcome</span>
          <strong>{props.work.sourceSnapshot.requiredChange}</strong>
        </div>
        <div>
          <span>Affected scope</span>
          <strong>{scopeLabel(props.work)}</strong>
        </div>
        <div>
          <span>Source</span>
          <A href={`/w/${props.work.websiteId}/brief`}>Open source Brief</A>
        </div>
      </section>

      <GovernActionBar
        work={props.work}
        brief={props.currentBrief}
        canVerify={canVerifyWork(props.work, props.currentBrief)}
        sourceReady={sourceReady()}
        onSetState={setState}
        onOpenEditor={props.onOpenEditor}
        onVerify={() =>
          props.onUpdate((work) => ({
            ...work,
            state: GOVERNANCE_STATE.VERIFIED,
            verifiedAt: new Date().toISOString(),
          }))
        }
      />

      <GovernRoles
        work={props.work}
        onEdit={(trigger) => props.onOpenEditor({ kind: "roles" }, trigger)}
      />
      <GovernChecks
        checks={props.work.checks}
        editable={canEditVerification(props.work, props.currentBrief)}
        readOnlyMessage={verificationEditingMessage(
          props.work,
          props.currentBrief,
        )}
        onEdit={(checkId, trigger) =>
          props.onOpenEditor({ kind: "check", checkId }, trigger)
        }
      />
      <Show when={props.work.supersededChecks.length > 0}>
        <details class="govern-superseded-checks">
          <summary>
            Superseded checks ({props.work.supersededChecks.length})
          </summary>
          <p>
            These checks no longer map to the current Brief. Their evidence has
            not been reused.
          </p>
        </details>
      </Show>
      <GovernDeliveryNotes
        workId={props.work.id}
        notes={props.work.deliveryNotes}
        onSave={(deliveryNotes) =>
          props.onUpdate((work) => ({ ...work, deliveryNotes }))
        }
      />

      <Show when={props.work.state === GOVERNANCE_STATE.NEEDS_REWORK}>
        <section class="govern-plan-section govern-rework-summary">
          <header>
            <div>
              <span class="signal-mini-kicker">Returned for correction</span>
              <h3>Rework required</h3>
            </div>
          </header>
          <p>{props.work.reworkReason}</p>
          <strong>Responsible: {props.work.reworkOwner}</strong>
        </section>
      </Show>

      <Show when={props.work.verifiedAt}>
        <section class="govern-plan-section govern-verification-result">
          <header>
            <div>
              <span class="signal-mini-kicker">Evidence accepted</span>
              <h3>Verification recorded</h3>
            </div>
          </header>
          <p>
            The supplied evidence satisfied the current Brief acceptance
            criteria. This does not claim that Faro changed the website.
          </p>
          <time dateTime={props.work.verifiedAt ?? undefined}>
            {formatSessionTime(props.work.verifiedAt)}
          </time>
        </section>
      </Show>

      <Show when={props.work.reviewSchedule}>
        {(schedule) => (
          <section class="govern-plan-section govern-review-plan">
            <header>
              <div>
                <span class="signal-mini-kicker">
                  {props.work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED
                    ? "Ongoing control"
                    : "Retained review context"}
                </span>
                <h3>
                  {props.work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED
                    ? "Next review"
                    : "Previous review plan"}
                </h3>
              </div>
              <Show
                when={
                  props.work.state === GOVERNANCE_STATE.VERIFIED ||
                  props.work.state === GOVERNANCE_STATE.REVIEW_SCHEDULED
                }
              >
                <button
                  type="button"
                  class="govern-text-action"
                  onClick={(event) =>
                    props.onOpenEditor(
                      { kind: "schedule" },
                      event.currentTarget,
                    )
                  }
                >
                  Edit schedule
                </button>
              </Show>
            </header>
            <Show when={props.work.state !== GOVERNANCE_STATE.REVIEW_SCHEDULED}>
              <p class="govern-review-inactive">
                This schedule is retained for context but is inactive until the
                work is verified again.
              </p>
            </Show>
            <dl>
              <div>
                <dt>Date</dt>
                <dd>{schedule().nextReviewDate}</dd>
              </div>
              <div>
                <dt>Owner</dt>
                <dd>{schedule().reviewOwner}</dd>
              </div>
              <div>
                <dt>Reason</dt>
                <dd>{schedule().reason}</dd>
              </div>
            </dl>
            <Show when={schedule().triggers.length > 0}>
              <div>
                <strong>Trigger events</strong>
                <ul>
                  {schedule().triggers.map((trigger) => (
                    <li>{trigger}</li>
                  ))}
                </ul>
              </div>
            </Show>
            <Show when={schedule().mustRemainTrue}>
              <p>
                <strong>Must remain true:</strong> {schedule().mustRemainTrue}
              </p>
            </Show>
          </section>
        )}
      </Show>
    </article>
  );
}

function scopeLabel(work: GovernWorkItem): string {
  const count = work.sourceSnapshot.pages.length;
  if (count === 0) return "Affected pages unavailable";
  return `${count} page${count === 1 ? "" : "s"}`;
}

function formatSessionTime(value: string | null): string {
  if (!value) return "";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
